import type { SiteLayer } from '../../types/site';
import {
  GENERATION_IMPACT_HASH_VERSION,
  GENERATION_IMPACT_LAYOUT_SCHEMA_VERSION,
  type GenerationImpactLayoutHead,
  type GenerationImpactLayoutPublication,
  type GenerationImpactScenarioSnapshot,
} from '../../types/generationImpact';
import { sha256HexForGenerationImpactValue } from './boundaryAuthority';
import { hasFirestoreNestedArrayWrapper } from './firestoreNestedArrayCodec';

/**
 * Stable JSON-compatible serialization shared by browser and server authority
 * checks. Firestore does not guarantee object insertion order, so map keys are
 * sorted recursively while array order remains significant.
 */
export function canonicalGenerationImpactJson(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) {
    return `[${value.map((row) => row === undefined ? 'null' : canonicalGenerationImpactJson(row)).join(',')}]`;
  }
  switch (typeof value) {
    case 'string': return JSON.stringify(value);
    case 'boolean': return value ? 'true' : 'false';
    case 'number': return Number.isFinite(value) ? JSON.stringify(value) : 'null';
    case 'object': {
      if (value instanceof Date) return JSON.stringify(value.toJSON());
      const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, child]) => child !== undefined && typeof child !== 'function' && typeof child !== 'symbol')
        .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0);
      return `{${entries.map(([key, child]) => `${JSON.stringify(key)}:${canonicalGenerationImpactJson(child)}`).join(',')}}`;
    }
    case 'undefined': return 'null';
    default: throw new TypeError(`Unsupported ${typeof value} value in Generation & Impact data`);
  }
}

/** SHA-256 of the canonical JSON representation, byte-compatible in Node and browsers. */
export function hashGenerationImpactValue(value: unknown): string {
  return sha256HexForGenerationImpactValue(canonicalGenerationImpactJson(value));
}

/** Reproduce the persisted v1 JSON.stringify digest exactly. */
export function hashGenerationImpactPayload(value: unknown): string {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError('Generation impact payload cannot be serialized for hashing.');
  }
  return sha256HexForGenerationImpactValue(serialized);
}

export interface GenerationImpactLayoutHashEntry {
  logicalKey: string;
  name: SiteLayer['name'];
  category: SiteLayer['category'];
  geometry: SiteLayer['geometry'];
  description: string | null;
}

/** Hash the exact fields that make up an immutable published layout body. */
export function hashGenerationImpactLayoutEntries(
  entries: readonly GenerationImpactLayoutHashEntry[],
): string {
  return hashGenerationImpactValue(entries);
}

/** Reconstruct the publication hash material from its frozen ordered layers. */
export function hashGenerationImpactPublicationLayers(layers: readonly SiteLayer[]): string {
  return hashGenerationImpactLayoutEntries(layers.map((layer) => {
    const logicalKey = layer.attributes?.layoutRole ?? layer.attributes?.generationImpactLogicalKey;
    if (typeof logicalKey !== 'string' || logicalKey.length === 0) {
      throw new Error(`Published layer ${layer.id || '<unknown>'} is missing its logical layout role`);
    }
    return {
      logicalKey,
      name: layer.name,
      category: layer.category,
      geometry: layer.geometry,
      description: layer.description ?? null,
    };
  }));
}

/** Return a fail-loud reason when stored scenario content no longer matches its hashes. */
export function generationImpactScenarioIntegrityReason(
  scenario: GenerationImpactScenarioSnapshot | null | undefined,
): string | null {
  if (!scenario?.input || !scenario.result) return 'Scenario snapshot is incomplete';
  if (scenario.hashVersion != null && scenario.hashVersion !== GENERATION_IMPACT_HASH_VERSION) {
    return `Scenario uses unsupported integrity hash version ${String(scenario.hashVersion)}`;
  }
  if (scenario.siteId !== scenario.input.siteId
    || scenario.name !== scenario.input.name
    || (scenario.targetPhaseId ?? undefined) !== (scenario.input.targetPhaseId ?? undefined)) {
    return 'Scenario snapshot identity differs from its hashed input';
  }
  try {
    // Legacy snapshots explicitly stamped with the v1 hash convention retain
    // their JSON.stringify digest for read/recovery compatibility. New
    // version-safe saves omit that legacy marker and use canonical JSON so a
    // Firestore map-key reorder cannot invalidate an immutable publication.
    const hashers = scenario.hashVersion == null
      ? [hashGenerationImpactValue, hashGenerationImpactPayload]
      : [hashGenerationImpactPayload];
    const comparisons = hashers.map(hashPayload => ({
      input: hashPayload(scenario.input) === scenario.inputHash,
      result: hashPayload(scenario.result) === scenario.resultHash,
    }));
    if (comparisons.some(match => match.input && match.result)) return null;
    // A body still carrying the Firestore persistence encoding can NEVER match
    // its hashes. Name the missing decode instead of reporting an
    // unexplainable content mismatch — mismatch branch only, so the happy path
    // pays nothing.
    if (hasFirestoreNestedArrayWrapper(scenario.input) || hasFirestoreNestedArrayWrapper(scenario.result)) {
      return 'Scenario content is still Firestore nested-array encoded; decode the snapshot before verifying its integrity hashes';
    }
    const inputMatches = comparisons.some(match => match.input);
    const resultMatches = comparisons.some(match => match.result);
    if (!inputMatches) {
      return 'Scenario input content does not match its integrity hash';
    }
    if (!resultMatches) {
      return 'Scenario result content does not match its integrity hash';
    }
    return 'Scenario input and result use different integrity hash conventions';
  } catch (error) {
    return `Scenario integrity could not be verified: ${(error as Error)?.message ?? 'invalid content'}`;
  }
  return null;
}

function sameStringArray(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

/** Compare every active-head identity field, including ordered layer IDs. */
export function sameGenerationImpactLayoutHeadAndPublication(
  head: GenerationImpactLayoutHead | null | undefined,
  publication: GenerationImpactLayoutPublication | null | undefined,
): boolean {
  return Boolean(
    head
      && publication
      && publication.schemaVersion === head.schemaVersion
      && publication.version === head.version
      && publication.publicationId === head.publicationId
      && publication.scenarioId === head.scenarioId
      && publication.scenarioName === head.scenarioName
      && publication.scenarioUpdatedAt === head.scenarioUpdatedAt
      && publication.inputHash === head.inputHash
      && publication.resultHash === head.resultHash
      && publication.modelVersion === head.modelVersion
      && publication.layoutHash === head.layoutHash
      && publication.publishedAt === head.publishedAt
      && publication.publishedBy === head.publishedBy
      && sameStringArray(publication.layerIds, head.layerIds),
  );
}

/**
 * Validate an immutable publication independently of its active site pointer.
 * This recomputes both scenario hashes and the layout-body hash, so copying the
 * old hash strings onto mutated geometry cannot satisfy the authority contract.
 */
export function generationImpactLayoutPublicationIntegrityReason(
  publication: GenerationImpactLayoutPublication | null | undefined,
): string | null {
  if (!publication) return 'Layout publication is missing';
  if (publication.schemaVersion !== GENERATION_IMPACT_LAYOUT_SCHEMA_VERSION) {
    return 'Layout publication uses an unsupported schema version';
  }
  const scenario = publication.scenarioSnapshot;
  const scenarioReason = generationImpactScenarioIntegrityReason(scenario);
  if (scenarioReason) return `Layout publication ${scenarioReason.toLowerCase()}`;
  if (publication.scenarioId !== scenario.id
    || publication.scenarioName !== scenario.name
    || publication.scenarioUpdatedAt !== scenario.updatedAt
    || publication.inputHash !== scenario.inputHash
    || publication.resultHash !== scenario.resultHash
    || publication.modelVersion !== scenario.result.modelVersion) {
    return 'Layout publication scenario snapshot differs from its publication identity';
  }
  if (!Array.isArray(publication.layerIds)
    || !Array.isArray(publication.layers)
    || publication.layerIds.length !== publication.layers.length
    || publication.layerIds.some((id, index) => publication.layers[index]?.id !== id)) {
    return 'Layout publication layer list is incomplete or out of order';
  }
  let recomputedLayoutHash: string;
  try {
    recomputedLayoutHash = hashGenerationImpactPublicationLayers(publication.layers);
  } catch (error) {
    return `Layout publication body cannot be verified: ${(error as Error)?.message ?? 'invalid layers'}`;
  }
  if (recomputedLayoutHash !== publication.layoutHash) {
    return 'Layout publication geometry or metadata does not match its integrity hash';
  }
  for (const layer of publication.layers) {
    const attrs = layer.attributes;
    const logicalKey = attrs?.layoutRole;
    if (layer.source !== 'scenario-derived'
      || attrs?.layoutKind !== 'generation-impact'
      || attrs.layoutPublicationId !== publication.publicationId
      || attrs.layoutVersion !== publication.version
      || attrs.generationImpactManaged !== true
      || attrs.generationImpactPublicationId !== publication.publicationId
      || attrs.generationImpactLayoutVersion !== publication.version
      || typeof logicalKey !== 'string'
      || attrs.generationImpactLogicalKey !== logicalKey
      || attrs.scenarioId !== scenario.id
      || attrs.scenarioInputHash !== scenario.inputHash
      || attrs.scenarioResultHash !== scenario.resultHash
      || attrs.inputHash !== scenario.inputHash
      || attrs.resultHash !== scenario.resultHash
      || attrs.modelVersion !== scenario.result.modelVersion
      || attrs.generationImpactLayoutHash !== publication.layoutHash) {
      return `Published layer ${layer.id} does not match the immutable publication tuple`;
    }
  }
  return null;
}
